import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { ThirdPartyScriptsContext } from "@ploy-ui/core";
import { useAppLoad } from "../appLoadContext";
import { useVariableData } from "@ploy-lib/calculation";
import { LoginResource, VulcanDataResource } from "@ploy-lib/rest-resources";
import { CustomerRole } from "@ploy-lib/types";
import { useCache, useResource } from "@rest-hooks/core";
import { usePageState } from "../PageContext";
const noopPushStep = (_step) => { };
const noopPushEvent = (_event, _submitData) => { };
export const useGTM = () => {
    const { google, settings } = useContext(ThirdPartyScriptsContext);
    const enabled = settings.loadGoogleTagManager;
    const appLoadData = useAppLoad();
    const { isAuthenticated } = useCache(LoginResource.status(), {}) ?? {};
    const vulcanData = useResource(VulcanDataResource.detail(), enabled && isAuthenticated ? {} : null);
    const loanAmount = useVariableData(enabled ? "Calculator" : undefined, enabled ? "LoanAmount" : undefined)?.value;
    const pushedEventsRef = useRef(new Set());
    const GTMData = useMemo(() => ({
        event: "VirtualPageview",
        product: appLoadData.productName,
        vendor: vulcanData?.vendorName,
        loanAmount
    }), [appLoadData.productName, vulcanData?.vendorName, loanAmount]);
    const pushEvent = useCallback((step) => {
        let virtualPageViewTitle = "";
        let virtualPageViewURL = "";
        let orderId = "";
        if (appLoadData.vulcanContext === "PostProcess") {
            virtualPageViewURL = `/bekreftelse`;
            virtualPageViewTitle = "Bekreftelse";
            orderId = appLoadData.applicationNumber?.split("-")?.[0] ?? "";
        }
        else {
            switch (step) {
                case 0:
                    virtualPageViewURL = `/finansiering`;
                    virtualPageViewTitle = "Finansiering";
                    break;
                case 1:
                    virtualPageViewURL = `/gjenstand`;
                    virtualPageViewTitle = "Gjenstand";
                    break;
                case 2:
                    virtualPageViewURL = `/kundeinfo`;
                    virtualPageViewTitle = "Kundeinfo";
                    break;
                case 3:
                    virtualPageViewURL = `/oppsummering`;
                    virtualPageViewTitle = "Oppsummering";
                    break;
                default:
                    break;
            }
        }
        // Avoid virtual pageview duplicates
        if (pushedEventsRef.current.has(virtualPageViewURL)) {
            return;
        }
        pushedEventsRef.current.add(virtualPageViewURL);
        google.push({
            ...GTMData,
            virtualPageViewURL,
            virtualPageTitle: virtualPageViewTitle,
            orderId
        });
        const hotjarUri = `/soknad/steg${step}`;
        //	Call hj() after pushing to dataLayer
        if (window["hj"]) {
            window["hj"]("vpv", hotjarUri);
        }
    }, [appLoadData.vulcanContext, appLoadData.applicationNumber, google, GTMData]);
    if (!enabled)
        return noopPushStep;
    return pushEvent;
};
export const useGTMExtended = () => {
    const appLoadData = useAppLoad();
    const { step, labels } = usePageState();
    const tabName = labels?.[step]?.toLocaleLowerCase() ?? "";
    const { google, settings } = useContext(ThirdPartyScriptsContext);
    const enabled = settings.loadGoogleTagManager;
    const objectCategoryId = useVariableData(enabled ? "Object" : undefined, enabled ? "CategoryCombined" : undefined).value;
    const { isAuthenticated } = useCache(LoginResource.status(), {}) ?? {};
    const vulcanData = useResource(VulcanDataResource.detail(), enabled && isAuthenticated ? {} : null);
    const loanAmount = useVariableData(enabled ? "Calculator" : undefined, enabled ? "LoanAmount" : undefined)?.value;
    const objectCategoriesOptions = useVariableData(enabled ? "Object" : undefined, enabled ? "options_CategoryCombined" : undefined);
    // the new application forms uses a dummy cosigner for the application during its initial state, so we can't check for cosignerid
    const coSignerPhoneMobile = useVariableData(enabled ? "CoSigner" : undefined, enabled ? "PhoneMobile" : undefined)?.value ?? "";
    const pushedEventsRef = useRef(new Set());
    //	Matches e.g. '"85|48"' to '{key: "85|48", value: "Campingvogn|Kjøretøy"}' => "Campingvogn":
    const selectedObjectCategory = Array.isArray(objectCategoriesOptions?.value)
        ? objectCategoriesOptions?.value
            ?.find((catCombined) => catCombined["key"].split("|")[0] === objectCategoryId?.split("|")[0])
            ?.value.split("|")[0]
        : undefined;
    const virtualPageViewURLPostfix = GTMCodeFromProductName(appLoadData.productName ?? "");
    const isCustomer = appLoadData.loggedInCustomerRole === CustomerRole.Customer;
    const isCoSigner = appLoadData.loggedInCustomerRole === CustomerRole.CoSigner;
    const isDirectCustomer = appLoadData.isDirectUser;
    const hasCoSigner = coSignerPhoneMobile !== "";
    let itemVariant = "forhandler_eller_agent";
    if (isCustomer)
        itemVariant = "hovedsøker";
    else if (isCoSigner)
        itemVariant = "medsøker";
    else if (isDirectCustomer)
        itemVariant = "hovedsøker";
    const GTMData = useMemo(() => ({
        ecommerce: {
            currency: "nok",
            value: loanAmount,
            items: [
                {
                    item_id: appLoadData.productId,
                    item_name: appLoadData.productName,
                    affiliation: vulcanData?.vendorName,
                    item_brand: appLoadData.vendorInfo?.salesChannel,
                    item_category: selectedObjectCategory,
                    item_variant: itemVariant,
                    price: loanAmount,
                    quantity: "1"
                }
            ]
        }
    }), [
        loanAmount,
        appLoadData.productId,
        appLoadData.productName,
        appLoadData.vendorInfo?.salesChannel,
        vulcanData?.vendorName,
        selectedObjectCategory,
        itemVariant
    ]);
    const pushEvent = useCallback((event = "", submitData) => {
        if (!event)
            return;
        // Clone to avoid mutating the memoized GTMData
        const data = {
            ...GTMData,
            ecommerce: {
                ...GTMData.ecommerce,
                items: [...GTMData.ecommerce.items]
            }
        };
        let checkout_step = 0;
        let virtualPageViewURL = "";
        let virtualPageViewTitle = "";
        let status = "sendt til kunde";
        if (isCustomer && hasCoSigner)
            status = "sendt til medsøker";
        else if (isCustomer || isCoSigner || isDirectCustomer)
            status = "innsendt";
        if (event === "purchase") {
            checkout_step = 4;
            data.ecommerce.transaction_id =
                submitData?.ApplicationNumber?.split("-")?.[0];
            data.ecommerce.application_date = new Date().toISOString().slice(0, 10);
            data.ecommerce.application_status = status;
            virtualPageViewURL = `/søknad-sendt${virtualPageViewURLPostfix}`;
            virtualPageViewTitle = "søknad-sendt";
        }
        else {
            if (event === "begin_checkout")
                checkout_step = 1;
            else if (event === "add_payment_info")
                checkout_step = 2;
            else if (event === "add_shipping_info")
                checkout_step = 3;
            virtualPageViewURL = `/${tabName}${virtualPageViewURLPostfix}`;
            virtualPageViewTitle = `${tabName}`;
        }
        // Avoid posting duplicate events
        if (pushedEventsRef.current.has(event) || data.ecommerce.value === 0) {
            return;
        }
        pushedEventsRef.current.add(event);
        google.push({
            ...data,
            event,
            checkout_step,
            virtualPageURL: virtualPageViewURL,
            virtualPageTitle: virtualPageViewTitle
        });
        //	Call hj() after pushing to dataLayer
        const hotjarUri = `/soknad${virtualPageViewURLPostfix}/steg${step}`;
        if (window["hj"]) {
            window["hj"]("vpv", hotjarUri);
        }
    }, [
        GTMData,
        google,
        isCustomer,
        isCoSigner,
        isDirectCustomer,
        hasCoSigner,
        virtualPageViewURLPostfix,
        tabName,
        step
    ]);
    if (!enabled)
        return noopPushEvent;
    return pushEvent;
};
export const HandlePageChangeForGTM = ({ step, labelsWithEvents }) => {
    const appLoadData = useAppLoad();
    const pushEvent = useGTM();
    const pushEventExtended = useGTMExtended();
    const { settings } = useContext(ThirdPartyScriptsContext);
    useEffect(() => {
        if (!settings.loadGoogleTagManager)
            return;
        if (settings.loadGoogleTagManagerExtended) {
            const pageEvents = labelsWithEvents.map(l => l.gtmPageEvent);
            if (pageEvents.some(l => !!l)) {
                const event = pageEvents[step];
                if (event)
                    pushEventExtended(event);
            }
            else if (!appLoadData.isDirectUser) {
                // Fallback to default events (dealerweb forms)
                if (step === 0) {
                    pushEventExtended("begin_checkout");
                }
                else if (step === 1)
                    pushEventExtended("add_payment_info");
                else if (step === 2)
                    pushEventExtended("add_shipping_info");
            }
        }
        else
            pushEvent(step);
    }, [
        appLoadData.isDirectUser,
        labelsWithEvents,
        pushEvent,
        pushEventExtended,
        settings.loadGoogleTagManager,
        settings.loadGoogleTagManagerExtended,
        step
    ]);
    return null;
};
const GTMCodeFromProductName = (productName) => {
    return ("-" +
        productName
            .toLowerCase()
            .replace(/ /g, "-")
            .replace(/æ/g, "ae")
            .replace(/ø/g, "oe")
            .replace(/å/g, "aa"));
};
//# sourceMappingURL=GoogleTagManagerHandler.js.map